/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 **/

const colors: Colors = {
	/** Primary */
	primary_90_100: "#29282E",
	primary_80_100: "#323338",
	primary_80_100_w8: "linear-gradient(0deg, #ffffff14, #ffffff14), #323338",
	primary_80_100_w12: "linear-gradient(0deg, #ffffff1f, #ffffff1f), #323338",
	primary_80_100_w16: "linear-gradient(0deg, #ffffff29, #ffffff29), #323338",
	primary_70_100: "#555961",

	/** Secondary */
	secondary_80_0: "#32333800",
	secondary_80_8: "#32333814",
	secondary_80_12: "#3233381F",
	secondary_80_15: "#32333826",
	secondary_80_16: "#32333829",
	secondary_80_20: "#32333833",
	secondary_80_24: "#3233383D",

	/** Neutral */
	neutral_90_100: "#1F1F21",
	neutral_80_100: "#2E2F32",
	neutral_70_100: "#44464A",
	neutral_60_100: "#6A6D73",
	neutral_50_100: "#878B93",
	neutral_40_100: "#AFB3BA",
	neutral_30_100: "#CACED4",
	neutral_20_100: "#E4E7ED",
	neutral_10_100: "#EDEEF1",
	neutral_00_100: "#FFFFFF",
	neutral_blue_80_100: "#313338",
	neutral_blue_70_100: "#3A3D43",
	neutral_blue_60_100: "#555961",

	/** Neutral with opacity */
	neutral_80_90: "#2E2F32E6",
	neutral_80_80: "#2E2F32CC",
	neutral_80_60: "#2E2F3299",
	neutral_80_50: "#2E2F3280",
	neutral_80_32: "#2E2F3252",
	neutral_80_16: "#2E2F3229",
	neutral_80_12: "#2E2F321F",

	neutral_50_4: "#878B930A",
	neutral_50_8: "#878B9314",
	neutral_50_12: "#878B931F",
	neutral_50_20: "#878B9333",
	neutral_50_30: "#878B934D",
	neutral_50_40: "#878B9366",
	neutral_50_80: "#878B93CC",

	neutral_10_80: "#EDEEF1CC",
	neutral_10_50: "#22222280",

	neutral_00_94: "#FFFFFFF0",
	neutral_00_88: "#FFFFFFE0",
	neutral_00_84: "#FFFFFFD6",
	neutral_00_80: "#FFFFFFCC",
	neutral_00_70: "#FFFFFFB2",
	neutral_00_68: "#FFFFFFAD",
	neutral_00_32: "#FFFFFF52",
	neutral_00_16: "#FFFFFF29",
	neutral_00_12: "#FFFFFF1F",
	neutral_00_8: "#FFFFFF14",

	/** Accent */
	accent_90_100: "#00006E",
	accent_80_100: "#181D8D",
	accent_70_100: "#3239A3",
	accent_60_100: "#4B52BC",
	accent_50_100: "#656CD7",
	accent_40_100: "#7F86F3",
	accent_30_100: "#9DA2F8",
	accent_20_100: "#BCC3F8",
	accent_10_100: "#D9DCF6",
	accent_00_100: "#EAEAF6",

	accent_60_80: "#4B52BCCC",
	accent_40_60: "#706AC8",
	accent_30_60: "#8886D2",
	accent_20_60: "#A9A8DF",

	/** Deco */
	deco1_10_100: "#F6E6E7",
	deco1_50_100: "#E4687E",
	deco2_10_100: "#FAE7ED",
	deco2_50_100: "#E070A2",
	deco3_10_100: "#F9E5F7",
	deco3_50_100: "#D66EBE",
	deco4_10_100: "#F1E2FB",
	deco4_50_100: "#B76DD4",
	deco5_10_100: "#E5E2F8",
	deco5_50_100: "#946DD4",
	deco6_10_100: "#DDE4F8",
	deco6_50_100: "#7074E3",
	deco7_10_100: "#DEEBFD",
	deco7_50_100: "#6282F2",
	deco8_10_100: "#E0ECF6",
	deco8_50_100: "#3A8FF2",
	deco9_10_100: "#DFF0F5",
	deco9_50_100: "#3A99CE",
	deco10_10_100: "#DBF1F2",
	deco10_50_100: "#23A3B5",
	deco11_10_100: "#DDF3DF",
	deco11_50_100: "#31A783",
	deco12_10_100: "#E1EED4",
	deco12_50_100: "#49A133",
	deco13_10_100: "#E6EFCB",
	deco13_50_100: "#7E9A0D",
	deco14_10_100: "#F5EDBE",
	deco14_50_100: "#C06B07",
	deco15_10_100: "#FAECD2",
	deco15_50_100: "#D9611D",
	deco16_10_100: "#FBEEDB",
	deco16_50_100: "#DF4615",
	deco17_10_100: "#FCE8DC",
	deco17_50_100: "#D63F1E",
	deco18_10_100: "#FAE5E1",
	deco18_50_100: "#B4443D",

	deco6_50_60: "#7074E399",
	deco6_50_80: "#7074E3CC",

	/** Success */
	success_90_100: "#006435",
	success_80_100: "#00844A",
	success_70_100: "#069656",
	success_60_100: "#14A863",
	success_50_100: "#1DB76E",
	success_40_100: "#4CC383",
	success_30_100: "#70CE99",
	success_20_100: "#9BDBB6",
	success_10_100: "#C3E9D2",
	success_00_100: "#E6F6ED",

	success_50_40: "#14A86366",
	success_50_60: "#14A86399",
	success_50_80: "#14A863CC",

	/** Error */
	error_90_100: "#B91529",
	error_80_100: "#C82335",
	error_70_100: "#D52A3C",
	error_60_100: "#E73443",
	error_50_100: "#EB4148",
	error_40_100: "#F0515C",
	error_30_100: "#F3707A",
	error_20_100: "#FEA4AA",
	error_10_100: "#FDCDD6",
	error_00_100: "#FFE8ED",

	error_00_80: "#FFE8EDCC",

	/** Warning */
	warning_90_100: "#E55314",
	warning_80_100: "#EF6C16",
	warning_70_100: "#F57C17",
	warning_60_100: "#FC8C19",
	warning_50_100: "#FF981A",
	warning_40_100: "#FFA731",
	warning_30_100: "#FFB753",
	warning_20_100: "#FFCC84",
	warning_10_100: "#FFE0B4",
	warning_00_100: "#FFF2DF",

	/** Gradient */
	// gradient_40
	gradient_40_100: "linear-gradient(225deg, #85CFB6 0%, #8EA1E3 50.12%, #CD77D3 100%)",
	gradient_40_100_w8:
		"linear-gradient(0deg, #ffffff14, #ffffff14), linear-gradient(225deg, #85CFB6 0%, #8EA1E3 50.12%, #CD77D3 100%)",
	gradient_40_100_w12:
		"linear-gradient(0deg, #ffffff1f, #ffffff1f), linear-gradient(225deg, #85CFB6 0%, #8EA1E3 50.12%, #CD77D3 100%)",
	gradient_40_100_w16:
		"linear-gradient(0deg, #ffffff29, #ffffff29), linear-gradient(225deg, #85CFB6 0%, #8EA1E3 50.12%, #CD77D3 100%)",
	gradient_40_100_w50:
		"linear-gradient(0deg, rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0.6)), linear-gradient(225deg, #85CFB6 0%, #8EA1E3 50.12%, #CD77D3 100%)",
	gradient_40_100_w60:
		"linear-gradient(0deg, rgba(255, 255, 255, 0.60) 0%, rgba(255, 255, 255, 0.60) 100%),linear-gradient(225deg, #85CFB6 0%, #8EA1E3 50.12%, #CD77D3 100%)",
	gradient_40_100_w80:
		"linear-gradient(0deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.8)), linear-gradient(225deg, #85CFB6 0%, #8EA1E3 50.12%, #CD77D3 100%)",

	// gradient_50
	gradient_50_100: "linear-gradient(225deg, #8EA1E3 0%, #AD98E8 50%, #C677D3 100%)",

	transparent: "transparent",

	// etc custom
	unity_loader_background: "#D8EEFF",
	chat_border: "#E1E3E8",
	title_color: "#000000",
	accepted_meeting: "rgba(188, 194, 248, 0.60)",
	bg_over_due_meeting: "rgba(50, 51, 56, 0.12)"
};

export default colors;
